import {ActionList, ConfirmationDialog} from '@primer/react'
import {ArchiveIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useRef} from 'react'
import {MutationFailureBanner} from '../FailureBanner'
import type {useArchiveActions} from './data/use-archive-actions'

export function ArchiveAction({actions}: {actions: ReturnType<typeof useArchiveActions>}) {
  const {canUnarchive, mutation, visible, confirmArchive, setConfirmArchive} = actions
  const descriptionRef = useRef<HTMLUListElement>(null)

  if (!visible && confirmArchive === null) return null

  const archive = confirmArchive ?? !canUnarchive
  const action = archive ? 'Archive' : 'Unarchive'
  const submit = () =>
    mutation.mutate(archive, {
      onSuccess: () => {
        announce(archive ? 'Pull request archived' : 'Pull request unarchived')
      },
    })

  return (
    <>
      <ActionList.Item
        variant={archive ? 'danger' : 'default'}
        loading={mutation.isPending}
        onSelect={() => {
          mutation.reset()
          setConfirmArchive(archive)
        }}
      >
        <ActionList.LeadingVisual>
          <ArchiveIcon />
        </ActionList.LeadingVisual>
        {action} pull request
      </ActionList.Item>
      {confirmArchive !== null && (
        <ConfirmationDialog
          title={`${action} this pull request?`}
          width="large"
          confirmButtonContent={`${action} pull request`}
          confirmButtonType={archive ? 'danger' : 'primary'}
          confirmButtonLoading={mutation.isPending}
          onClose={gesture => {
            if (mutation.isPending) return
            if (gesture === 'confirm') {
              submit()
            } else {
              setConfirmArchive(null)
              mutation.reset()
            }
          }}
        >
          <MutationFailureBanner
            title={`Failed to ${action.toLowerCase()} pull request`}
            mutation={mutation}
            returnFocusRef={descriptionRef}
          />
          {archive ? (
            <ul className="ml-3" ref={descriptionRef} tabIndex={-1}>
              <li>The pull request will be closed.</li>
              <li>Only users with permission to manage archived pull requests will be able to see it.</li>
              <li>A user with that permission can unarchive it at any time.</li>
            </ul>
          ) : (
            <ul className="ml-3" ref={descriptionRef} tabIndex={-1}>
              <li>The pull request will remain closed, and its conversation lock state will not change.</li>
              <li>It will be visible to all users with repository access again.</li>
              <li>You can reopen it or unlock its conversation separately if you have permission.</li>
            </ul>
          )}
        </ConfirmationDialog>
      )}
    </>
  )
}
